/* oxlint-disable max-lines */
// TODO: задача на рефакторинг https://jira.cian.tech/browse/CD-189369
import { v4 as uuid } from 'uuid';

import { CHATBOT_ID } from '../constants/chatbotId';
import { IOffer } from '../repositories/chats/entities/get_my_offers_for_selection/Offer';
import { isMessageRead } from './message/isMessageRead';
import { updateMessageFromRaw } from './message/updateMessageFromRaw';
import { ERequestApiStatus } from '../types/appState';
import { TMessageFiles } from '../types/messageFiles';
import { TMessageType, EOutcomingStatus, TMessage, TMessageSchema, TOutcomingStatus, TReply } from '../types/messages';
import { TOpenedChats } from '../types/openedChats';

// Массив даты [Год, Месяц, Число]
type TTimePage = [number, number, number];

export interface IParsedMessage {
  message: TMessage;
  messageId: string;
  chatId: string;
  // Идентификатор в виде Год/Месяц/Число
  timePageKey: string;
  timePage: TTimePage;
  uid: string;
  rawMessage: TMessageSchema;
}

/**
 * Элемент Map'а сообщений, который в себе содержит "адрес" сообщения в странице
 * и распаршенное сообщение.
 */
export interface IMessageItem {
  // индекс сообщения в массиве сообщений страницы
  messageIndex: number;
  // распаршенное сообщение
  message: IParsedMessage;
}

export type TMessagesMap = Map<string, IMessageItem>;

// При загрузке сообщения на бек мы формируем messageId через uuid,
// обратно по веб-сокету это значение возвращается в поле uid, а messageId формирует бек
function parseMessage(rawMessage: TMessageSchema, userId: number, status?: TOutcomingStatus): IParsedMessage {
  const {
    assumptions,
    bubbles,
    actionButtons,
    actionButtonsV3,
    chatId,
    communicationId,
    files,
    fromChatBot,
    fromCrm,
    htmlText,
    images,
    messageId,
    offers,
    rowVersion,
    text,
    type,
    uid,
    userId: messageUserId,
    created,
    status: rawStatus,
    reply,
  } = rawMessage;

  const date = created ? new Date(created) : null;

  const timePage: TTimePage = date
    ? [date.getFullYear(), date.getMonth(), date.getDate()]
    : ([] as unknown as TTimePage);

  // TODO Объеденить с проверкой в файле src/shared/containers/ChatMessageContainer/hooks/useOutcomingMessageStyle.ts
  const isOutcoming = Boolean(messageUserId === userId || (chatId === CHATBOT_ID && !fromChatBot));
  const isRead = isMessageRead(rawMessage, isOutcoming);

  return {
    messageId,
    /**
     * uid - клиентский идентификатор, сгенерированный перед отправкой сообщения (см. createOutcomingMessage).
     * Сервер возвращает его обратно в WS-событии, чтобы клиент мог сопоставить эхо с оптимистичным сообщением.
     * У входящих сообщений от других пользователей и у сообщений из истории uid отсутствует —
     * в этом случае в качестве стабильного идентификатора используется серверный messageId.
     */
    uid: uid || messageId,
    chatId,
    timePage,
    timePageKey: `${timePage[0]}/${timePage[1]}/${timePage[2]}`,
    rawMessage,
    message: {
      actionButtons,
      actionButtonsV3,
      assumptions,
      bubbles,
      communicationId,
      created: new Date(created).getTime(),
      files,
      fromChatBot,
      fromCrm,
      htmlText,
      images,
      isOutcoming,
      message: text || '',
      offers,
      rowVersion,
      type,
      userId: messageUserId,
      isDeleted: rawStatus === 'deleted',
      reply,
      ...(isOutcoming
        ? {
            status: isRead ? EOutcomingStatus.Read : status || EOutcomingStatus.Fulfilled,
          }
        : {
            isRead,
            status: undefined,
          }),
    },
  };
}

export interface IMessagePage {
  date: number;
  messages: IParsedMessage[];
  // Идентификатор в виде Год/Месяц/Число
  timeCode: string;
}

interface IOutcomingMessageParams {
  chatId: string;
  userId: number;
  type?: TMessageType;
  text?: string;
  payload?: string | null;
  files?: TMessageFiles;
  offers?: IOffer[];
  uid?: string;
  read?: boolean;
  reply?: TReply | null;
}

interface IPrepareMessageReplyParams {
  messagesMap: TMessagesMap;
  replyToMessageId?: string | null;
}

export function prepareMessageReply({
  messagesMap,
  replyToMessageId: messageId,
}: IPrepareMessageReplyParams): TReply | undefined {
  if (!messageId) {
    return;
  }

  const chatRepliedMessage = messageId ? messagesMap.get(messageId) : undefined;
  const repliedMessage = chatRepliedMessage?.message?.message;

  if (!repliedMessage) {
    return;
  }

  const { userId: authorId, message: quoteText = '', files } = repliedMessage;

  /** Оффер */
  if (repliedMessage.type === 'offer') {
    return {
      type: 'offer',
      messageId,
      authorId,
      quoteText,
    };
  }

  /** Фото как документ */
  if (files?.imagesAsDocuments?.length) {
    const { uid: quoteFileUid, name: quoteFileName } = files.imagesAsDocuments[0];

    return {
      type: 'photo',
      messageId,
      authorId,
      quoteFileUid,
      quoteFileName,
      quoteText: quoteFileName,
    };
  }

  /** Фото */
  if (files?.images?.length) {
    const { uid: quoteFileUid, name: quoteFileName } = files.images[0];

    return {
      type: 'photo',
      messageId,
      authorId,
      quoteFileUid,
      quoteFileName,
      quoteText,
    };
  }

  /** Документ */
  if (files?.documents?.length) {
    return {
      type: 'document',
      messageId,
      authorId,
      quoteText,
    };
  }

  /** Текст */
  return {
    type: 'text',
    messageId,
    authorId,
    quoteText,
  };
}

export function createOutcomingMessage({
  text,
  chatId,
  userId,
  type = 'text',
  uid,
  read = false,
  reply,
}: IOutcomingMessageParams): TMessageSchema {
  const messageId = uid || uuid();

  return {
    created: new Date().toISOString(),
    text,
    userId,
    messageId,
    uid: messageId,
    chatId,
    status: read ? 'readByRecipient' : 'deliveredToServer',
    unread: !read,
    type,
    actionButtons: [],
    actionButtonsV3: [],
    assumptions: undefined,
    offers: [],
    bubbles: [],
    rowVersion: 0,
    rowVersionV2: '',
    reply,
  };
}

const cloneChatMessages = (chatMessages: IParsedMessage[]) => chatMessages.slice();

export function setMessageReadStatus(messagesMap: TMessagesMap, messageIds: string[], chatMessages: IParsedMessage[]) {
  const nextChatMessages = cloneChatMessages(chatMessages);

  messageIds.forEach(id => {
    const messageItem = messagesMap.get(id);

    if (messageItem) {
      const { message, messageIndex } = messageItem;

      const nextParseMessage: IParsedMessage = {
        ...message,
        message: {
          ...message.message,
          ...(message.message.isOutcoming ? { status: 'read' } : { isRead: true }),
        },
      };
      messagesMap.set(id, {
        messageIndex,
        message: nextParseMessage,
      });

      if (nextChatMessages[messageIndex]) {
        nextChatMessages[messageIndex] = nextParseMessage;
      } else {
        console.error(`Couldn't set 'read' status for the message. Message not found.`);
      }
    }
  });

  return nextChatMessages;
}

const updateMessageIndexInMap = (chatMessages: IParsedMessage[], messagesMap: TMessagesMap) =>
  chatMessages
    .sort((a, b) => a.message.created - b.message.created)
    .forEach((message, messageIndex) =>
      messagesMap.set(message.messageId, {
        message,
        messageIndex,
      }),
    );

export function removeMessageFromChatMessages(
  messageId: string,
  messagesMap: TMessagesMap,
  chatMessages: IParsedMessage[],
) {
  const nextChatMessages = cloneChatMessages(chatMessages);

  const messageItemToDelete = messagesMap.get(messageId);

  // если есть сообщение по запрошенному id, удалить его - оно вставиться заново с новой датой.
  if (messageItemToDelete) {
    // перестраховка
    const indexMessageItemToDelete = nextChatMessages.findIndex(message => message.messageId === messageId);

    nextChatMessages.splice(indexMessageItemToDelete, 1);

    messagesMap.delete(messageId);

    // индексы сообщений поменялись, обновляем все что после удаленного сообщения
    for (let i = indexMessageItemToDelete; i < nextChatMessages.length; i++) {
      const message = nextChatMessages[i];

      messagesMap.set(message.messageId, {
        message,
        messageIndex: i,
      });
    }
  }

  return nextChatMessages;
}

function handleRawMessage(
  rawMessage: TMessageSchema,
  messagesMap: TMessagesMap,
  chatMessages: IParsedMessage[],
  userId: number,
  status?: TOutcomingStatus,
  checkRowVersion?: boolean,
): boolean {
  const messageItem = messagesMap.get(rawMessage.messageId);

  // если сообщение есть, значит пришло обновление о прочитанном статусе либо получателем, либо отправителем
  if (messageItem) {
    const { message, messageIndex } = messageItem;

    // обновляем только если обновился rowVersion
    if (!!checkRowVersion && message.message.rowVersion === rawMessage.rowVersion) {
      return false;
    }

    // если понадобится когда-то возможность редактирования сообщения, то это надо сделать тут.
    const parsedMessage: IParsedMessage = {
      ...message,
      rawMessage, // Сохраняем полные данные с бэкенда для отката
      message: updateMessageFromRaw(message.message, rawMessage),
    };

    messagesMap.set(rawMessage.messageId, {
      message: parsedMessage,
      messageIndex,
    });

    if (chatMessages[messageIndex]) {
      chatMessages[messageIndex] = parsedMessage;
    }

    return false;
  }

  const parsedMessage = parseMessage(rawMessage, userId, status);

  chatMessages.push(parsedMessage);

  return true;
}

export function insertMessageInChatMessages(
  messagesMap: TMessagesMap,
  rawMessage: TMessageSchema,
  chatMessages: IParsedMessage[],
  userId: number,
  status?: TOutcomingStatus,
) {
  const nextChatMessages = cloneChatMessages(chatMessages);

  const indexesChanged = handleRawMessage(rawMessage, messagesMap, nextChatMessages, userId, status);

  if (indexesChanged) {
    updateMessageIndexInMap(nextChatMessages, messagesMap);
  }

  return nextChatMessages;
}

function updateMessagesInChat(
  messagesMap: TMessagesMap,
  rawMessages: TMessageSchema[],
  chatMessages: IParsedMessage[],
  userId: number,
  checkRowVersion?: boolean,
) {
  const nextChatMessages = cloneChatMessages(chatMessages);

  let indexesChanged = false;

  rawMessages.forEach(
    rawMessage =>
      (indexesChanged =
        handleRawMessage(rawMessage, messagesMap, nextChatMessages, userId, undefined, checkRowVersion) ||
        indexesChanged),
  );

  if (indexesChanged) {
    updateMessageIndexInMap(nextChatMessages, messagesMap);
  }

  return nextChatMessages;
}

type TAddMessagesInChatParams = {
  openedChats: TOpenedChats;
  messages: TMessageSchema[];
  currentOpenedChatId?: string;
  userId: number;
  messagesMap: TMessagesMap;
};
type TSynchronizationMessagesInChatParams = {
  openedChats: TOpenedChats;
  messages: TMessageSchema[];
  chatId?: string;
  userId: number;
  messagesMap: TMessagesMap;
};

export const addMessagesInChat = ({
  openedChats,
  messages,
  currentOpenedChatId,
  userId,
  messagesMap,
}: TAddMessagesInChatParams): TOpenedChats => {
  if (!currentOpenedChatId) {
    return openedChats;
  }

  const currentOpenedChat = openedChats[currentOpenedChatId];

  if (!currentOpenedChat) {
    return openedChats;
  }

  if (!messages.length) {
    return {
      ...openedChats,
      [currentOpenedChatId]: {
        ...currentOpenedChat,
        allMessagesLoaded: true,
        status: ERequestApiStatus.Succeed,
      },
    };
  }

  const chatMessages = updateMessagesInChat(messagesMap, messages, currentOpenedChat.chatMessages, userId);

  return {
    ...openedChats,
    [currentOpenedChatId]: {
      ...currentOpenedChat,
      currentTimestamp: chatMessages[0]?.message.created,
      chatMessages,
      status: ERequestApiStatus.Succeed,
    },
  };
};

export const synchronizationMessagesInChat = ({
  openedChats,
  messages,
  chatId,
  userId,
  messagesMap,
}: TSynchronizationMessagesInChatParams): TOpenedChats => {
  if (!chatId) {
    return openedChats;
  }

  const currentChat = openedChats[chatId];

  if (!currentChat) {
    return openedChats;
  }

  if (!messages.length) {
    return {
      ...openedChats,
      [chatId]: {
        ...currentChat,
        status: ERequestApiStatus.Succeed,
      },
    };
  }

  // TODO - при переходе на v2 - добавить возможность удалять сообщение
  const chatMessages = updateMessagesInChat(messagesMap, messages, currentChat.chatMessages, userId, true);

  return {
    ...openedChats,
    [chatId]: {
      ...currentChat,
      currentTimestamp: chatMessages[0]?.message.created,
      chatMessages,
      status: ERequestApiStatus.Succeed,
    },
  };
};

export function handleRawMessageForNotification(
  rawMessage: TMessageSchema,
  messagesMap: TMessagesMap,
  userId: number,
  status?: TOutcomingStatus,
  checkRowVersion?: boolean,
): { parsedMessage: IParsedMessage; isExists: boolean } {
  const messageItem = messagesMap.get(rawMessage.messageId);

  // если сообщение есть, значит пришло обновление о прочитанном статусе либо получателем, либо отправителем
  if (messageItem) {
    const { message, messageIndex } = messageItem;

    // обновляем только если обновился rowVersion
    if (!!checkRowVersion && message.message.rowVersion === rawMessage.rowVersion) {
      return { parsedMessage: message, isExists: true };
    }

    // если понадобится когда-то возможность редактирования сообщения, то это надо сделать тут.
    const parsedMessage: IParsedMessage = {
      ...message,
      rawMessage, // Сохраняем полные данные с бэкенда для отката
      message: updateMessageFromRaw(message.message, rawMessage),
    };

    messagesMap.set(rawMessage.messageId, {
      message: parsedMessage,
      messageIndex,
    });

    return { parsedMessage, isExists: true };
  }

  const parsedMessage = parseMessage(rawMessage, userId, status);

  return { parsedMessage, isExists: false };
}
